<template>
  <div
    v-loading="loadingState.isLoading"
    :element-loading-text="loadingState.loadingText"
    class="h-full py-15 px-20 mobile-l:py-20 mobile-l:px-25 tablet:py-25 tablet:px-35"
  >
    <h2 class="text-xl text-primary leading-tight mb-10 tablet:text-2xl laptop:mb-5">
      Choose a package that's right for you
    </h2>
    <p class="leading-tight mb-15 laptop:mb-40">{{ descriptionLabel }}</p>

    <Transition
      name="fade-medium"
      mode="out-in"
    >
      <PackageOptions
        v-if="currencyCode && currentEvent"
        type="event-upgrade"
        :active-package="activePackage"
        :disabled-package-ids="disabledPackageIds"
        :packages="packagesOneOffs"
        :currency-code="currencyCode"
        @continue="onUpgradeContinue($event)"
      >
        <span class="font-medium text-lg tracking-wide">
          Continue
        </span>
      </PackageOptions>
    </Transition>
  </div>
</template>

<script>
import LogRocket from 'logrocket';
import { mapState, mapGetters, mapActions } from 'vuex';
import { FSGetUserEvent } from '@/helpers/firestoreRefs';
import { APIGetCurrencyCodeByIP, APIGetUpgradeEventSession } from '@/api';
import loadExternalScript from '@/utils/loadExternalScript';
import { defaultCurrencyCode } from '@/utils/intl';
import { showErrorNotification } from '@/utils/notification';
import PackageOptions from '@/components/PackageOptions';

export default {
  props: {
    eventID: {
      type: String,
      default: '',
    },
  },
  components: {
    PackageOptions,
  },
  data: () => ({
    unsubUserEvent: null,
    stripeRef: null,
    currencyCode: '',
    upgradeContinueIsLoading: false,
  }),
  created() {
    this.unsubUserEvent = this.subUserEvent(this.eventID);
  },
  mounted() {
    loadExternalScript('https://js.stripe.com/v3/')
      .then(() => {
        const { Stripe } = window;

        this.stripeRef = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
      })
      .catch((error) => {
        console.error(error);
      });
  },
  watch: {
    'currentEvent.title': {
      immediate: true,
      handler(newValue) {
        this.updateCurrentNavigationTitleBase(`Upgrading: ${newValue || 'event'}`);
      },
    },
    userIsLoading: {
      immediate: true,
      handler(newValue) {
        if (newValue) return;

        if (this.userDetails.currencyCode) {
          this.currencyCode = this.userDetails.currencyCode;
          return;
        }

        APIGetCurrencyCodeByIP()
          .then((response) => {
            this.currencyCode = response.data.currencyCode.toLowerCase();
          })
          .catch((error) => {
            this.currencyCode = defaultCurrencyCode;
            console.error(error);
          });
      },
    },
  },
  computed: {
    ...mapState('packages', {
      packageIsInitialLoading: 'isInitialLoading',
    }),
    ...mapState('user', {
      userIsLoading: 'isLoading',
      userDetails: 'details',
    }),
    ...mapState('user/events', {
      eventLoadingStatus: 'loadingStatus',
      allEvents: 'all',
    }),
    ...mapGetters('packages', {
      packagesOneOffs: 'oneOffs',
    }),
    currentEvent() {
      if (this.eventLoadingStatus === 'init') return null;
      return this.allEvents[this.eventID];
    },
    activePackage() {
      if (!this.currentEvent?.packageId || this.packageIsInitialLoading) return null;

      const eventPackageIndex = this.packagesOneOffs
        .findIndex((packageDoc) => packageDoc.id === this.currentEvent.packageId);
      if (eventPackageIndex === -1) {
        return null;
      }
      return this.packagesOneOffs[eventPackageIndex];
    },
    disabledPackageIds() {
      if (!this.activePackage) return [];

      const disabledPackageIds = [];

      for (let i = 0; i < this.packagesOneOffs.length; i += 1) {
        if (this.packagesOneOffs[i].id === this.activePackage.id) break;

        disabledPackageIds.push(this.packagesOneOffs[i].id);
      }

      return disabledPackageIds;
    },
    descriptionLabel() {
      if (!this.activePackage) {
        // Upgrade (From Free event)
        return 'Pick a larger package in case you expect more guests at the event';
      }

      // Upgrade (From existing package)
      return 'You can upgrade your purchased package at any time';
    },
    loadingState() {
      if (this.eventLoadingStatus === 'init') {
        return {
          isLoading: true,
          loadingText: 'Loading your Event..',
        };
      }
      if (!this.currencyCode) {
        return {
          isLoading: true,
          loadingText: 'Loading latest packages..',
        };
      }
      if (this.upgradeContinueIsLoading) {
        return {
          isLoading: true,
          loadingText: 'Redirecting you to the payment..',
        };
      }

      return {
        isLoading: false,
        loadingText: '',
      };
    },
  },
  methods: {
    ...mapActions('navigation', {
      updateCurrentNavigationTitleBase: 'updateCurrentNavigationTitleBase',
    }),
    ...mapActions('user/events', {
      onUserEventSnapshot: 'onEventSnapshot',
    }),
    subUserEvent(eventId) {
      return FSGetUserEvent(eventId)
        .onSnapshot((eventSnapshot) => {
          this.onUserEventSnapshot(eventSnapshot);
        }, (error) => {
          LogRocket.captureException(error);
          console.error(error);
        });
    },
    onUpgradeContinue({ packageId, currencyCode }) {
      if (!this.$route.params.eventID) return;

      this.upgradeContinueIsLoading = true;

      APIGetUpgradeEventSession({
        eventId: this.$route.params.eventID,
        packageId,
        stripeCheckout: {
          currencyCode,
          successUrl: `http://${window.location.host}/event/${this.$route.params.eventID}`,
          cancelUrl: window.location.href,
        },
      }).then((result) => {
        this.stripeRef.redirectToCheckout({
          sessionId: result.data.stripeSessionId,
        }).then((redirectResult) => {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
          if (redirectResult.error.message) {
            this.upgradeContinueIsLoading = false;
            console.error(redirectResult.error);
            showErrorNotification(redirectResult.error.message);
          }
          console.log(redirectResult);
        });
      }).catch((error) => {
        this.upgradeContinueIsLoading = false;
        console.error(error.response.data.message);
        showErrorNotification(error.response.data.message);
      });
    },
  },
  beforeDestroy() {
    if (this.unsubUserEvent !== null) this.unsubUserEvent();
  },
};
</script>

<style scoped>
</style>
